import { useContext } from 'react';
import { useStore } from 'zustand';
import { ArenaStoreState } from './arenaStoreTypes';
import { createArenaStore, ArenaStoreApi } from './createArenaStore';
import { ArenaStoreContext, getTableStore, ArenaStoreProvider, useArenaStoreApi } from './ArenaStoreContext';

let _defaultArenaStore: ArenaStoreApi | null = null;

/**
 * Gets or lazily initializes the default fallback store instance.
 */
export const getDefaultArenaStore = (): ArenaStoreApi => {
  if (!_defaultArenaStore) {
    _defaultArenaStore = createArenaStore();
  }
  return _defaultArenaStore;
};

/**
 * Default fallback store instance for tests and components outside of ArenaStoreProvider.
 * Lazily evaluated via Proxy to prevent circular module evaluation TDZ crashes.
 */
export const defaultArenaStore: ArenaStoreApi = new Proxy({} as ArenaStoreApi, {
  get(_target, prop) {
    const store = getDefaultArenaStore();
    const value = (store as any)[prop];
    return typeof value === 'function' ? value.bind(store) : value;
  }
});

/**
 * Universal useArenaStore hook.
 *
 * If inside <ArenaStoreProvider tableId={...}>, selects from the isolated table store.
 * If outside (or in unit tests), transparently falls back to the default/active store.
 */
export function useArenaStore<T = ArenaStoreState>(
  selector: (state: ArenaStoreState) => T = (s) => s as unknown as T
): T {
  const contextStore = useContext(ArenaStoreContext);
  const store = contextStore ?? getDefaultArenaStore();
  return useStore(store, selector);
}

// Static imperative API on the hook (fully backwards compatible with useArenaStore.getState(), etc.)
useArenaStore.getState = (targetTableId?: string | null): ArenaStoreState => {
  const activeStore = getTableStore(targetTableId) ?? getDefaultArenaStore();
  return activeStore.getState();
};

useArenaStore.setState = (
  partial: Partial<ArenaStoreState> | ((state: ArenaStoreState) => Partial<ArenaStoreState>),
  replace?: boolean,
  targetTableId?: string | null
): void => {
  const activeStore = getTableStore(targetTableId) ?? getDefaultArenaStore();
  (activeStore.setState as any)(partial, replace);
};

useArenaStore.subscribe = (
  listener: (state: ArenaStoreState, prevState: ArenaStoreState) => void,
  targetTableId?: string | null
) => {
  const activeStore = getTableStore(targetTableId) ?? getDefaultArenaStore();
  return activeStore.subscribe(listener);
};

// Re-export context, provider, factory, and types
export { createArenaStore, ArenaStoreProvider, useArenaStoreApi, getTableStore };
export type { ArenaStoreApi };
export * from './arenaStoreTypes';
